import React, { useMemo, useEffect, useRef } from "react";
import { Select } from "antd";
import * as R from "ramda";
import { compose } from "recompose";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { alpha3ToAlpha2 } from "i18n-iso-countries";

import { isLoading } from "../../../../../utils/actions";
import { normalizeText } from "../../../../../utils/candidates";
import { getCountries } from "../../../../../utils/data/static/countries";
import {
  // fetchCountries,
  fetchProvinces,
  fetchDistricts,
  // FETCH_COUNTRIES,
  FETCH_PROVINCES,
  FETCH_DISTRICTS
} from "../../../../../store/actions/catalogs";

import { Container, Title } from "./FilterComponents";

import styled from "styled-components/macro";

const sortOptions = R.sortBy(
  R.compose(
    R.toLower,
    R.prop("label")
  )
);

const Subtitle = styled.h5`
  margin-top: ${({ first }) => (first ? "none" : "15px")};
`;

function AddressFilter({
  loading,
  setCountry,
  setProvince,
  setDistrict,
  country = [],
  province = [],
  district = [],
  provinces = [],
  districts = [],
  fetchProvinces,
  fetchDistricts,
  loadingProvinces,
  loadingDistricts
}) {
  const refCountry = useRef();
  const refProvince = useRef();
  const refDistrict = useRef();
  const { t } = useTranslation();
  const anySelected = useMemo(
    () => country.length || province.length || district.length,
    [country.length, district.length, province.length]
  );

  useEffect(() => {
    if (country.length && !provinces.length && !loadingProvinces) {
      fetchProvinces(country.map(alpha3ToAlpha2));
    }
    if (province.length && !districts.length && !loadingDistricts) {
      fetchDistricts(province);
    }
  }, [
    country,
    province,
    fetchProvinces,
    fetchDistricts,
    loadingProvinces,
    loadingDistricts,
    provinces.length,
    districts.length
  ]);

  const countryOptions = R.pipe(
    getCountries,
    R.toPairs,
    R.map(([value, label]) => ({ value, label })),
    sortOptions
  )();

  const provinceOptions = R.pipe(
    R.map(({ id, nombre }) => ({ value: id, label: nombre })),
    sortOptions
  )(provinces);

  const districtOptions = R.pipe(
    R.map(({ id, nombre }) => ({ value: id, label: nombre })),
    sortOptions
  )(districts);

  const filterOption = (inputValue, option) =>
    normalizeText(option.props.children).includes(normalizeText(inputValue));

  // console.log(country, province, district);

  return (
    <details open={anySelected}>
      <Title>
        <h4>{t("opportunity:candidates.addressFilter.title")}</h4>
      </Title>
      <Container>
        <Subtitle first={true}>
          {t("opportunity:candidates.addressFilter.country")}
        </Subtitle>
        <Select
          ref={refCountry}
          mode="multiple"
          value={country}
          showSearch
          allowClear={true}
          loading={loading}
          placeholder={t("opportunity:candidates.addressFilter.placeholder")}
          optionFilterProp="children"
          filterOption={filterOption}
          onChange={value => {
            // Close dropdown list
            refCountry.current.blur();
            setCountry(value);
            if (value.length) {
              fetchProvinces(value.map(alpha3ToAlpha2));
            }
          }}
        >
          {countryOptions.map(({ value, label }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
        <Subtitle>
          {t("opportunity:candidates.addressFilter.province")}
        </Subtitle>
        <Select
          ref={refProvince}
          mode="multiple"
          value={province}
          showSearch
          allowClear={true}
          loading={loading || loadingProvinces}
          disabled={country.length === 0}
          placeholder={t("opportunity:candidates.addressFilter.placeholder")}
          optionFilterProp="children"
          filterOption={filterOption}
          onChange={value => {
            // Close dropdown list
            refProvince.current.blur();
            setProvince(value);
            if (value.length) {
              fetchDistricts(value);
            }
          }}
        >
          {provinceOptions.map(({ value, label }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
        <Subtitle>
          {t("opportunity:candidates.addressFilter.district")}
        </Subtitle>
        <Select
          ref={refDistrict}
          mode="multiple"
          value={district}
          showSearch
          allowClear={true}
          loading={loading || loadingDistricts}
          disabled={province.length === 0}
          placeholder={t("opportunity:candidates.addressFilter.placeholder")}
          optionFilterProp="children"
          filterOption={filterOption}
          onChange={value => {
            // Close dropdown list
            refDistrict.current.blur();
            setDistrict(value);
          }}
        >
          {districtOptions.map(({ value, label }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      </Container>
    </details>
  );
}

const enhancer = compose(
  connect(
    state => {
      return {
        provinces: FETCH_PROVINCES.getResponse(state) || [],
        districts: FETCH_DISTRICTS.getResponse(state) || [],
        loadingProvinces: isLoading(FETCH_PROVINCES, state),
        loadingDistricts: isLoading(FETCH_DISTRICTS, state)
      };
    },
    {
      fetchProvinces,
      fetchDistricts
    }
  )
);

export default enhancer(AddressFilter);
